import { useEffect, useState } from 'react'
import { LogIn } from 'lucide-react'
import Spinner from './Spinner'
import { authMeUrl } from '../lib/authMeUrl'
import './EmbeddedLogin.css'

/**
 * Login affordance for the embeddable /p/ routes. The embed runs in a cross-site iframe, so
 * Auth0 can't be navigated to inside the frame (it refuses framing) and the session cookie is
 * a third-party cookie. This opens the login flow in a popup (top-level context), best-effort
 * requests storage access so the resulting cookie is usable inside the frame, and polls
 * /api/auth-me until the session is established — then reloads the frame to load the data.
 */
export default function EmbeddedLogin({ message }: { message?: string }) {
  const [waiting, setWaiting] = useState(false)
  const [popupBlocked, setPopupBlocked] = useState(false)

  async function handleLogin() {
    // Best-effort: in browsers that block third-party cookies, ask for storage access under
    // this user gesture so the frame can use its first-party cookie after login.
    const doc = document as Document & { requestStorageAccess?: () => Promise<void> }
    if (typeof doc.requestStorageAccess === 'function') {
      try { await doc.requestStorageAccess() } catch { /* not granted — popup login still works */ }
    }

    const returnTo = window.location.pathname + window.location.search
    const url = `/api/auth-login?returnTo=${encodeURIComponent(returnTo)}`
    const popup = window.open(url, 'loopqa-login', 'width=500,height=720')
    if (!popup) {
      setPopupBlocked(true)
      return
    }
    setPopupBlocked(false)
    setWaiting(true)
  }

  useEffect(() => {
    if (!waiting) return
    const check = async () => {
      try {
        const res = await fetch(authMeUrl())
        if (res.ok) {
          const data = (await res.json()) as { user: unknown }
          if (data.user) window.location.reload()
        }
      } catch {
        // keep polling
      }
    }
    const interval = window.setInterval(check, 2000)
    return () => window.clearInterval(interval)
  }, [waiting])

  return (
    <div className="embedded-login" data-testid="embedded-login">
      <p className="embedded-login__message">{message || 'Log in to view this project.'}</p>
      <button
        type="button"
        className="embedded-login__button"
        onClick={handleLogin}
        disabled={waiting}
        data-testid="embedded-login-button"
        data-analytics-event="embed-login-signin"
      >
        {waiting ? (
          <><Spinner size={16} className="embedded-login__spinner" /> Waiting for login…</>
        ) : (
          <><LogIn size={16} aria-hidden /> Log in</>
        )}
      </button>
      {waiting && (
        <p className="embedded-login__hint">
          Finish logging in in the popup window — this view refreshes automatically once you're signed in.
        </p>
      )}
      {popupBlocked && (
        <p className="embedded-login__error" role="alert">
          Your browser blocked the login popup. Allow popups for this site, then try again.
        </p>
      )}
    </div>
  )
}
